import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './RelatedArticles.css';

function RelatedArticles({ currentArticleId }) {
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Supported languages
  const supportedLanguages = ['it', 'en', 'es', 'de', 'fr', 'pt'];

  // Extract current language from URL or use i18n current language
  const getCurrentLanguage = () => {
    // First check URL path for language code
    const pathParts = location.pathname.split('/');
    if (pathParts.length > 1 && supportedLanguages.includes(pathParts[1])) {
      return pathParts[1];
    }
    // Fallback to i18n current language
    return i18n.language || 'it';
  };

  const currentLang = getCurrentLanguage();

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      try {
        // Fetch all articles for the current language
        const response = await fetch(`/data/json/article/${currentLang}/list.json`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const allArticles = await response.json();

        // Filter out the current article (compare as strings to handle both number and string ids)
        const availableArticles = allArticles.filter(article =>
          String(article.id) !== String(currentArticleId)
        );

        // Select 3 random articles
        const randomArticles = getRandomArticles(availableArticles, 3);

        setRelatedArticles(randomArticles);
      } catch (error) {
        console.error('Error fetching related articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [currentArticleId, currentLang]);

  // Function to get n random articles from an array
  const getRandomArticles = (articles, n) => {
    // Make a copy to avoid modifying the original array
    const shuffled = [...articles];

    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Return the first n articles from the shuffled array
    return shuffled.slice(0, n);
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (relatedArticles.length === 0) {
    return null; // Don't show the section if there are no related articles
  }

  // Helper function to format categories (handles both string and array)
  const formatCategories = (categories) => {
    if (!categories) return '';
    if (Array.isArray(categories)) {
      return categories.join(', ');
    }
    // If it's a string, return as is
    return categories;
  };

  return (
    <section className="related-articles">
      <h3 className="related-articles-title">{t('article.relatedArticles', 'Ti potrebbe interessare anche')}</h3>
      <div className="related-articles-grid">
        {relatedArticles.map(article => (
          <article key={article.id} className="related-article-card">
            <Link
                to={`/${currentLang}/article/${article.url || `${article.id}-${article.title.toLowerCase().replace(/[^\w]+/g, '-')}`}`}
                onClick={e => {
                    e.preventDefault();
                    window.location.href = `/${currentLang}/article/${article.url || `${article.id}-${article.title.toLowerCase().replace(/[^\w]+/g, '-')}`}`;
                }}
            >
              <div className="related-article-image">
                <img
                  src={`/data/json/article/img/${article.id}.png`}
                  alt={article.title}
                  loading="lazy"
                />
              </div>
              <div className="related-article-content">
                <h4>{article.title}</h4>
                <p>{article.subtitle}</p>
                <div className="related-article-meta">
                  {article.categories && (
                    <span className="related-article-category">
                      {formatCategories(article.categories)}
                    </span>
                  )}
                  {article.characters && article.characters[0] && (
                    <span className="related-article-character">
                      {article.characters[0].name}
                    </span>
                  )}
                    {article.characters && article.characters[1] && (
                        <span className="related-article-character">
                          {article.characters[1].name}
                        </span>
                    )}
                </div>
              </div>
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
}

export default RelatedArticles;
